import {useState, FC} from "react";
import {useTranslation} from "next-i18next";
import {formatDistanceToNow, parseISO} from "date-fns";
import {StyledRating} from "../styled-rating";
import {Typography, Box, Grid, Link} from "@mui/material";
import {UserAvatar} from "../user-avatar";
import {FormattedCertification} from "../formatted-certification";
import {DiveNumber} from "../dive-number";
import NextLink from "next-translate-routes/link";
import {useAuth} from "../../hooks/use-auth";
import {ReviewInterface} from "seacrush-core"
import {
    calculateSingleRatingForReview,
    openEditUpsertDialog,
    deleteReview, setVerifiedReview
} from "../../slices/reviews-slice";
import {useDispatch} from "../../store";


const round = (number: any) => {
    if (isNaN(number)) {
        return "?";
    } else {
        return number.toFixed(1);
    }
};


interface ReviewProps {
    review: ReviewInterface
}


export const Review: FC<ReviewProps> = ({review}) => {

    const [showOriginal, setShowOriginal] = useState(false);

    const {i18n} = useTranslation()

    const {user, isAuthenticated, isAdmin} = useAuth();

    const dispatch = useDispatch()


    const {
        id,
        by,
        scores,
        body,
        verified,
        timeAdded,
        translations,
        detectedLang,
        detectedLangName
    } = review;

    const siteLang = i18n.language

    const {t} = useTranslation();

    let scoreAvg = calculateSingleRatingForReview(review)

    // @ts-ignore
    const couldTranslate = siteLang !== detectedLang && !!translations && !!translations[siteLang];

    const showTranslation = (!showOriginal && couldTranslate);

    // @ts-ignore
    const bodyToShow = showTranslation ? translations[siteLang] : body;

    return (
        <Box sx={{pb: 1, flexGrow: 1}}>
            <Grid
                container
                direction={"row"}
                alignItems={"flex-start"}
                justifyContent={"space-evenly"}
                wrap={"nowrap"}
            >
                <Grid item>
                    <div style={{marginLeft: 14, paddingRight: 8}}>
                        <UserAvatar
                            slimUser={{
                                photoUrl: by.photoUrl,
                                username: by.username,
                                homeCountryCode: by.homeCountryCode,
                                accepted: by.accepted
                            }}
                        />
                    </div>
                </Grid>
                <Grid
                    container
                    direction={"column"}
                    alignItems={"stretch"}
                    justifyContent={"flex-start"}
                >
                    <Grid item style={{paddingTop: 12}}>
                        <NextLink
                            href={{
                                pathname: "/u/[username]",
                                query: {username: by.username}
                            }}>
                            <span>
                            {by.firstName} {by.lastName ? by.lastName.slice(0, 1) : ""}
                                </span>
                        </NextLink>{" "}
                        <span style={{color: "#757575"}}>
              {verified && (
                  <>
                      &mdash; <em>{t("verified-review")}</em>
                  </>
              )}
            </span>
                    </Grid>
                    <Grid item>
                        <Box>
                            <Box
                                textAlign={"center"}
                                display={"inline-block"}
                            >
                                <FormattedCertification
                                    certLevel={by.certLevel}/>
                                <DiveNumber
                                    numberOfDives={by.numberOfDives}/>
                            </Box>
                        </Box>
                    </Grid>
                    <Grid item sx={{pt: 2, pb: 1,}}>
                        <Box mb={2} borderColor='transparent'>
                            <StyledRating
                                name='review-score-avg'
                                value={scoreAvg / 2}
                                size={"small"}
                                readOnly
                                max={5}
                                precision={0.1}
                            />
                            <Typography
                                style={{
                                    top: -2,
                                    color: "rgb(102,102,102)",
                                    position: "relative",
                                    marginLeft: 4
                                }}
                                variant={"body1"}
                                component={"span"}
                            >
                                <strong>{scoreAvg}</strong>/10{" "}
                                <Box
                                    sx={{
                                        display: {
                                            xs: {},
                                            sm: "inline-block"
                                        },
                                        "&::before": {

                                            content: {
                                                sm: "'\\2014'"
                                            }
                                        }
                                    }}
                                >
                                    {" "}
                                    {formatDistanceToNow(parseISO(timeAdded), {
                                        addSuffix: true
                                    })}
                                </Box>
                            </Typography>
                        </Box>

                        <div style={{
                            ...(couldTranslate && {
                                borderLeft: "2px solid #dcdee3",
                                paddingLeft: 12
                            })
                        }}>
                            <Typography>
                                {showTranslation && <>
                                    <a href='#' onClick={event => {
                                        event.preventDefault();
                                        setShowOriginal(true);
                                    }}>{t("show-original", {detectedLangName})}</a><br/>
                                </>}

                                {couldTranslate && showOriginal && <>
                                    <a href='#' onClick={event => {
                                        event.preventDefault();
                                        setShowOriginal(false);
                                    }}>{t("show-translation")}</a><br/>
                                </>}
                            </Typography>

                            <Typography
                                variant={"body1"}
                                sx={{
                                    whiteSpace: "pre-wrap",
                                    pr: 1
                                }}
                            >

                                {bodyToShow}
                            </Typography>
                        </div>
                    </Grid>
                </Grid>
            </Grid>
            <Box display='flex' justifyContent='flex-end'>
                {isAdmin && (
                    <>
                        <Link
                            variant={"subtitle2"}
                            href='#'
                            onClick={e => {
                                e.preventDefault();
                                dispatch(setVerifiedReview(review.id, !review.verified))
                            }}
                        >
                            {" "}
                            [toggle verified]
                        </Link>
                        &nbsp;
                        <Link
                            variant={"subtitle2"}
                            href='#'
                            onClick={e => {
                                e.preventDefault();
                                dispatch(deleteReview(review.id))
                            }}
                        >
                            {" "}
                            [delete]
                        </Link>
                        &nbsp;
                    </>
                )}
                {(isAdmin || by.uid === user?.uid) && (
                    <>
                        <Link
                            variant={"subtitle2"}
                            href='#'
                            onClick={() => dispatch(openEditUpsertDialog(review))}
                        >
                            {" "}
                            [{t("edit")}]
                        </Link>
                        &nbsp;
                    </>
                )}
            </Box>
        </Box>
    );
}
